/* External */
import {
  memo, useCallback, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Set } from 'immutable';

/* Ott-common */
import { PROVIDER } from '@dtvgo/backend-service/normalize';

/* Components */
import VirtualizedList, { ItemDataProvider } from '../VirtualizedList';
import CarouselRow from './CarouselRow';

/* utils */
import {
  getCardType, getCarouselHeight, getInitialCardType, sizeByViewport,
} from '../../utils/cardsUtils';

/* hooks */
import useFetchPage from '../../hooks/useFetchPage';
import usePageCarousels from '../../hooks/usePageCarousels';

/* Styles */
import './CarouselList.scss';

function CarouselList({
  pageId,
  sportIds,
  height,
  onScroll,
  stealFocusOnMount,
  itemsContainerClassName,
  className,
}) {
  const [fetchedCarousels, setFetchedCarousels] = useState(() => Set());
  const [wasCarouselFocused, setWasCarouselFocused] = useState(false);
  const {
    page,
    page: { carousels: carouselsInfo } = {},
  } = useFetchPage(pageId, PROVIDER.VRIO, sportIds);
  const carouselsFromStore = usePageCarousels(page);

  const handleCarouselFetchComplete = useCallback((carouselId) => {
    setFetchedCarousels((current) => current.add(carouselId));
  }, []);

  const updateWasCarouselFocused = useCallback(() => {
    setWasCarouselFocused(true);
  }, []);

  const getCardTypeByIndex = useCallback((index) => {
    const carouselInfo = carouselsInfo?.get(index);
    const carouselFromStore = carouselsFromStore?.get(index);
    const cardType = getCardType(carouselFromStore);
    const initialCardType = getInitialCardType(carouselInfo);
    return carouselFromStore ? cardType : initialCardType;
  }, [carouselsFromStore, carouselsInfo]);

  const getItemSize = useCallback((index) => sizeByViewport(
    getCarouselHeight(getCardTypeByIndex(index)) ?? -1,
  ), [getCardTypeByIndex]);

  const firstNonEmptyCarousel = useMemo(() => {
    if (!carouselsInfo) return 0;

    let index = 0;
    while (getItemSize(index) === 0 && index < carouselsInfo?.size) index += 1;
    return index;
  }, [carouselsInfo, getItemSize]);

  const itemData = useMemo(() => ({
    carouselsInfo,
    pageId,
    fetchedCarousels,
    stealFocusOnMount: stealFocusOnMount && !wasCarouselFocused,
    firstNonEmptyCarousel,
    updateWasCarouselFocused,
    handleCarouselFetchComplete,
  }), [
    carouselsInfo,
    pageId,
    fetchedCarousels,
    stealFocusOnMount,
    wasCarouselFocused,
    firstNonEmptyCarousel,
    updateWasCarouselFocused,
    handleCarouselFetchComplete,
  ]);

  return (
    <ItemDataProvider itemData={itemData}>
      <VirtualizedList
        className={`dtv-html5-c-carousel-list ${className}`}
        height={height}
        itemCount={carouselsInfo?.size || 0}
        itemSize={getItemSize}
        renderer={CarouselRow}
        onScroll={onScroll}
        itemsContainerClassName={itemsContainerClassName}
      />
    </ItemDataProvider>
  );
}

CarouselList.propTypes = {
  pageId: PropTypes.string,
  sportIds: PropTypes.shape({}),
  height: PropTypes.number,
  onScroll: PropTypes.func,
  stealFocusOnMount: PropTypes.bool,
  itemsContainerClassName: PropTypes.string,
  className: PropTypes.string,
};

CarouselList.defaultProps = {
  pageId: '',
  sportIds: {},
  height: sizeByViewport(1080),
  onScroll: undefined,
  stealFocusOnMount: false,
  itemsContainerClassName: '',
  className: '',
};

export default memo(CarouselList);
